import '../../scss/pages/_wheel.scss';
import '../../scss/pages/_not-wheel.scss';
import '../../scss/pages/_battle-pass-new.scss';

import dayjs from 'dayjs';
import { setTimeout } from 'isomorphic-timers-promises';
import _, { findIndex, map, sample } from 'lodash';
import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useToggle } from 'react-use';
import { useConfig } from '~components/hooks';
import { Loader } from '~components/Loader';
import { useOpenModal } from '~components/modals/hooks';
import { CircleControls, Prizes } from '~components/wheel';
import { QuestList } from '~frontend/components/battlePass/questList';
import { usePrizes } from '~components/wheel/hooks';
import playAudio from '~frontend/utils/playAudio';
import { useHttpClient } from '~shared/frontend/providers/HttpProvider';
import {
  GET_BATTLE_PASS,
  useGetBattlePass,
  useNextLevelId,
} from '~components/hooks/useGetBattlePass';
import {
  Config as ConfigType,
  Profile as ProfileType,
  WheelGame as WheelGameType,
  WheelQuest as WheelQestType,
} from '~types';

import { gql, useMutation, useQuery } from '@apollo/client';
import Currency from '~frontend/components/Currency';
import { LazyLoadImage } from 'react-lazy-load-image-component';

interface WheelData {
  getConfig: ConfigType;
  getProfile: ProfileType;
  getWheelQuests: WheelQestType[];
}

interface WheelMutationData {
  signWheel: any & WheelGameType;
}

interface WheelMutationVars {
  input: {
    ids: number[];
  };
}

interface ILettersCounter {
  A: number;
  C: number;
  S: number;
  E: number;
}

let audioInterval = null;
let abortController = null;

const Wheel: FunctionComponent = () => {
  //const d = useGetBattlePass({ id: 4 });
  const { setHttpContext } = useHttpClient();
  const [cookies, setCookie, removeCookie] = useCookies([
    'wheelModal',
    'redirect',
    'wheelPrizeData',
  ]);
  const { onShow } = useOpenModal();
  const letters = ['C', 'S', 'C', 'A', 'S', 'E'];
  const getConfig = useConfig();
  const [isActive, setActive] = useToggle(false);
  const navigate = useNavigate();
  const [selectedCaseId, setSelectedCaseId] = useState<number>(0);
  const isMobile =
    getConfig?.userAgent.match(
      /Android|BlackBerry|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i,
    ) ?? false;

  const [style, setStyle] = useState({});
  const [showDesclaimer, setShowDesclaimer] = useState(true);
  const [lettersCounter, setLettersCounter] = useState({
    A: 0,
    C: 0,
    S: 0,
    E: 0,
  } as ILettersCounter);
  const [isHover, setIsHover] = useState<boolean>(false);

  const FETCH_CASE_BY_ID = gql`
    query getCase($input: CaseInput!) {
      getCase(input: $input) {
        id
        getName
        getUrl
        getImage
        getBackgroundImage
      }
    }
  `;

  const { loading, error, data, refetch } = useQuery<WheelData>(
    gql`
      query Wheel {
        getConfig {
          wheelCasesPrizes
        }
        getProfile {
          id
          getWheelLetters
          getWheelStep
          lastWheelGame
          wheelCoins
          getWheelCasesPrizes
          getExternalAccounts {
            accountId
            userName
            service
            props
            getUrl
          }
        }
        getWheelQuests {
          id
          quest {
            id
            getName
            getCoins
            props
            type
            typeDate
          }
          getWheelUserQuest {
            id
            isAwait
            isCompleted
            props
            wheelQuestId
          }
        }
      }
    `,
  );

  const handleQuestComplete = () => {
    refetch(); // Trigger refetch when a quest completes
  };

  const {
    loading: caseLoading,
    error: caseError,
    data: caseData,
  } = useQuery(FETCH_CASE_BY_ID, {
    variables: { input: { id: selectedCaseId } },
    skip: !selectedCaseId, // Skip the query if no ID is selected
  });

  const [updateWheelCasesPrizes] = useMutation(
    gql`
      mutation updateWheelCasesPrizes($id: Int!, $wheelCasesPrizes: [Int!]!) {
        updateWheelCasesPrizes(id: $id, wheelCasesPrizes: $wheelCasesPrizes) {
          success
        }
      }
    `,
  );

  const [updateFreeCase] = useMutation(
    gql`
      mutation updateFreeCase($id: Int!, $caseId: Int!) {
        updateFreeCase(id: $id, caseId: $caseId) {
          success
        }
      }
    `,
  );

  const handleMouseEnter = (event: React.MouseEvent) => {
    setIsHover(true);
    playAudio(event, '4.wav', 0.5);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const getCasePrize = async () => {
    const prizes = data?.getProfile?.getWheelCasesPrizes;
    if (prizes && prizes.length !== 0) {
      const caseId = prizes[prizes.length - 1];
      setSelectedCaseId(caseId);
    } else {
      try {
        const newPrizes = _.shuffle(data?.getConfig?.wheelCasesPrizes);
        await updateWheelCasesPrizes({
          variables: {
            id: data?.getProfile?.id,
            wheelCasesPrizes: newPrizes,
          },
        });
        setSelectedCaseId(newPrizes[newPrizes.length - 1]);
      } catch (e) {
        console.log('ERROPR', e);
      }
    }
  };

  const checkFullWord = async () => {
    if (
      lettersCounter.A > 0 &&
      lettersCounter.C > 1 &&
      lettersCounter.E > 0 &&
      lettersCounter.S > 1
    ) {
      await updateFreeCase({
        variables: {
          id: data?.getProfile?.id,
          caseId: selectedCaseId,
        },
      });
      return true;
    } else return false;
  };

  // Trigger this effect when `selectedCaseId` changes
  useEffect(() => {
    const handleCheckFullWord = async () => {
      if (!caseLoading && !caseError && caseData) {
        const isFullWord = await checkFullWord();
        if (isFullWord) {
          console.log('caseData', caseData);
          const getCase = caseData.getCase;
          onShow('WheelPrize', {
            isCasePrize: true,
            image: getCase.getImage,
            caseId: getCase.id,
            caseName: getCase.getName,
            caseUrl: getCase.getUrl,
            endDate: dayjs().add(1, 'day').format(),
          });
        }
      } else if (caseError) {
        console.log('Error123', caseError);
      }
    };

    handleCheckFullWord(); // Trigger the async function when effect runs
  }, [caseData, caseLoading, caseError]);

  useEffect(() => {
    if (
      loading ||
      !data ||
      !data.getProfile ||
      !data.getProfile?.getWheelLetters
    ) {
      return; // Return early if data is not ready
    }

    // Only proceed if data, getProfile, and getWheelLetters are all properly defined.
    if (data?.getProfile?.getWheelLetters?.length > 0) {
      const newLettersCounter: ILettersCounter = { A: 0, C: 0, S: 0, E: 0 };

      // Iterate through the letters and count occurrences.
      data?.getProfile?.getWheelLetters.forEach((letter: string) => {
        const upperLetter = letter.toUpperCase();
        if (upperLetter === 'A') newLettersCounter.A += 1;
        else if (upperLetter === 'C') newLettersCounter.C += 1;
        else if (upperLetter === 'S') newLettersCounter.S += 1;
        else if (upperLetter === 'E') newLettersCounter.E += 1;
      });

      // Update state with the new letters count.
      setLettersCounter(newLettersCounter);
    }

    // Check for the full word only if the data exists.
    const handleGetCasePrize = async () => {
      await getCasePrize();
    };
    handleGetCasePrize();
  }, [data, loading]);

  useEffect(() => {
    abortController = new AbortController();
    const storedPrizeData = cookies.wheelPrizeData;

    const handlePrizeData = async () => {
      if (storedPrizeData) {
        const { getPrize, data } = storedPrizeData;

        if (data.type === 1) {
          if (getPrize?.showPopup || cookies.wheelModal) {
            onShow('WheelPrize', {
              image: getPrize.image,
              promoCode: data.value.promo.code,
              endDate: dayjs().add(1, 'day').format(),
            });
          } else {
            navigate('/pay', {
              state: {
                promoCode: data.value.promo.code,
              },
            });
          }
        }
      }

      removeCookie('wheelPrizeData');
    };

    handlePrizeData().catch((error) => {
      console.error('Error handling prize data:', error);
    });

    return () => {
      clearInterval(audioInterval);
      abortController.abort();
    };
  }, []);

  const [prizes] = usePrizes();

  const { t } = useTranslation();

  const [signWheel] = useMutation<WheelMutationData, WheelMutationVars>(
    gql`
      mutation SignWheel($input: SignWheelInput!) {
        signWheel(input: $input) {
          id
          prizeId
          type
          value
        }
      }
    `,
  );

  const getProfile = data?.getProfile;
  const getWheelQuests = data?.getWheelQuests;
  //const getBattlePass = d.data?.getBattlePass;
  console.log('quests', getWheelQuests);

  const onDesclaimerClose = () => {
    setShowDesclaimer(false);
  };

  const onSignWheel = async (event: SyntheticEvent<HTMLDivElement>) => {
    try {
      if (event) {
        event.preventDefault();
      }

      if (isActive) {
        return null;
      }

      setActive(true);

      if (!getProfile?.getWheelStep) {
        throw Error('wheel_autorization_error');
      } else if (getProfile?.getWheelStep === 1) {
        setCookie('redirect', '/wheel', {
          expires: dayjs().add(5, 'minutes').toDate(),
        });

        return document.getElementById('vk_login').click();
      }

      window?.dataLayer?.push({
        event: 'run_wheel',
      });

      const {
        data: { signWheel: data },
      } = await signWheel({ variables: { input: { ids: map(prizes, 'id') } } });
      const prizeData = {
        getPrize: _.find(prizes, ['id', data?.prizeId]),
        data,
      };

      setCookie('wheelPrizeData', prizeData);

      setStyle({});

      await setTimeout(400, '', { signal: abortController.signal });

      const rouletteItems = document.getElementsByClassName(
        'circle-roulette-item',
      );

      const index = findIndex(
        rouletteItems,
        ({ dataset: { id } }: HTMLElement) =>
          Number(id) === Number(data.prizeId),
      );

      const rotate = 720 * 3 - 30 * index;

      const randomNumbers = [-10, -7, -5, 5, 7, 10];
      const finalRotate = rotate + sample(randomNumbers);

      setStyle({
        transform: `rotate(${finalRotate}deg)`,
        transition: 'transform 15s cubic-bezier(0.63, 0.03, 0.67, 0.98) 0s',
      });

      let prevAngle = 0;

      audioInterval = setInterval(() => {
        const el = document.getElementsByClassName('circle-roulette')[0];
        const st = window.getComputedStyle(el, null);
        const tr =
          st.getPropertyValue('-webkit-transform') ||
          st.getPropertyValue('-moz-transform') ||
          st.getPropertyValue('-ms-transform') ||
          st.getPropertyValue('-o-transform') ||
          st.getPropertyValue('transform') ||
          'fail...';

        if (tr !== 'none') {
          let values: any = tr.split('(')[1];
          values = values.split(')')[0];
          values = values.split(',');

          const a = Number(values[0]);
          const b = Number(values[1]);

          let radians = Math.atan2(b, a);
          if (radians < 0) {
            radians += 2 * Math.PI;
          }

          const angle = Math.round(radians * (180 / Math.PI));

          if (Math.abs(angle - prevAngle) > 30) {
            playAudio(null, 'roulette-start-2.mp3', 0.1);
            prevAngle = angle;
          }
        }
      });

      await setTimeout(15000, '', { signal: abortController.signal });

      clearInterval(audioInterval);
      setActive(false);

      if (data.type === 1) {
        await setTimeout(4500, '', { signal: abortController.signal });

        const getPrize = _.find(prizes, ['id', data?.prizeId]);

        if (getPrize?.showPopup || cookies?.wheelModal) {
          onShow('WheelPrize', {
            image: getPrize.image,
            promoCode: data.value.promo.code,
            endDate: dayjs().add(1, 'day').format(),
          });
          removeCookie('wheelPrizeData');
        } else {
          navigate('/pay', {
            state: {
              promoCode: data.value.promo.code,
            },
          });
          removeCookie('wheelPrizeData');
        }
      }

      removeCookie('wheelPrizeData');

      await refetch();
    } catch (error) {
      if (!abortController.signal.aborted) {
        // if (getProfile?.getWheelStep === 2) {
        //   document.getElementById('vk_subscribe').click();
        // } else if (getProfile?.getWheelStep === 5) {
        //   document.getElementById('senler_subscribe').click();
        // }

        setActive(false);

        if (
          error.graphQLErrors &&
          error.graphQLErrors.find(
            (error) => error.extensions?.code === 'NOT_ENOUGH_COINS',
          )
        ) {
          const questListElement = document.querySelector(
            '.battle-pass-quest-list',
          );
          if (questListElement) {
            questListElement.scrollIntoView({ behavior: 'smooth' });
          }
        }

        toast.error(
          <>
            <div className="notify-title">{t('Error')}!</div>
            <div className="notify-text">{t(error.message)}</div>
          </>,
        );
      }
    }
  };

  // if (isMobile) {
  //   setHttpContext({ redirect: '/' });
  //   return;
  // }

  if (loading && caseLoading) {
    return <Loader />;
  } else if (error) {
    return <>Wheel | Error! {error.message}</>;
  }

  return (
    <>
      <Helmet>
        <title>
          {t('CS GO bonus wheel! GGdrop drum is free!', {
            hostname: getConfig?.hostname,
          })}
        </title>
        <meta
          name="description"
          content={t(
            'Spin every day a free wheel of cs go bonuses! Get top skins and promotional codes! Barban bonuses CSCASE access to everyone immediately after authorization!',
            {
              hostname: getConfig?.hostname,
            },
          )}
        />
      </Helmet>
      <div className="page-circle-game-wrap">
        <div className="page-circle-game">
          <div
            className={`circle-wrap ${
              getProfile?.getWheelStep === 3 && !isActive && 'play'
            }`}
          >
            {showDesclaimer && (
              <div className="circle-message">
                <div className="battle-pass-quest-list-desclaimer">
                  <div
                    className="icon material-icons"
                    data-icon="close"
                    onClick={onDesclaimerClose}
                  ></div>
                  <div
                    className="icon material-icons"
                    data-icon="feedback"
                  ></div>
                  {!getProfile && (
                    <>
                      {' '}
                      <div className="battle-pass-quest-list-desclaimer-title">
                        Тебе не хватает <span>CSCase Coins</span> для прокрутки
                        - Авторизуйся
                      </div>
                      <div className="battle-pass-quest-list-desclaimer-text">
                        <p>{t('Wheel_spin_costs_1000_Coins_')}</p>
                        <p>
                          {t('Log_in_and_get_1000_Coins_to_spin_the_wheel_')}
                        </p>
                      </div>
                    </>
                  )}
                  {getProfile &&
                    getProfile.wheelCoins < getConfig?.wheelSpinCost && (
                      <>
                        <div className="battle-pass-quest-list-desclaimer-title">
                          {t('Get_bonuses_wheel')}{' '}
                          {/* <span>CSCase Coins</span> */}
                        </div>
                        <div className="battle-pass-quest-list-desclaimer-text">
                          {/* <p>{t('Quests_are_updated_daily_')}</p> */}
                        </div>
                      </>
                    )}
                  {getProfile &&
                    getProfile.wheelCoins >= getConfig?.wheelSpinCost && (
                      <>
                        <div className="battle-pass-quest-list-desclaimer-title">
                          {t('You_already_have_enough_')}{' '}
                          <span>CSCase Coins</span>!
                        </div>
                        <div className="battle-pass-quest-list-desclaimer-text">
                          <p>
                            {t('Hurry_and_spin_the_wheel_to_claim_your_bonus_')}
                          </p>
                        </div>
                      </>
                    )}
                </div>
              </div>
            )}

            <div className="circle-user-coins">
              <div className="circle-user-coins-val">
                {getProfile ? getProfile?.wheelCoins : 0}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.99902 12V8.00097H9.99926V12H6.99902ZM12.0002 12V8.00097H15.0002V12H12.0002ZM12.0002 20.001V17.001H9.99926V20.001H6.99902V17.001H3.99902L0 12.999V6.99902L3.99902 3H6.99902V-0.000976562H9.99926V3H12.0002V-0.000976562H15.0002V3H18.0002L19.9985 6H5.00097L2.99999 8.00098V12L5.00097 14.001H19.9985L18.0002 17.001H15.0002V20.001L12.0002 20.001Z"
                    fill="#F59EFF"
                  ></path>
                </svg>
              </div>

              <div className="circle-user-coins-cost">
                {!getProfile && getConfig?.wheelSpinCost}
                {getProfile && getProfile?.wheelCoins < getConfig?.wheelSpinCost
                  ? getConfig?.wheelSpinCost - getProfile?.wheelCoins
                  : ''}
                {(!getProfile ||
                  getProfile?.wheelCoins < getConfig?.wheelSpinCost) && (
                  <>
                    <svg
                      width="13"
                      height="12"
                      viewBox="0 0 13 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4.01132 7.99973V3.99923H5.01212V7.99973H4.01132ZM8.01963 7.99973V3.99923H9.01953V7.99973H8.01963ZM8.01573 11.9992V9.99941H5.01602V11.9992H4.01523V9.99941H2.01543L0.015625 7.99961V3.99911L2.01543 1.99931H4.01523V-0.000488281H5.01602V1.99931H8.01573V-0.000488281H9.01563V1.99931H11.0154L12.0153 2.99921H3.01532L1.01552 4.99991V6.99971L3.01532 8.99951H12.0153L11.0154 9.99941H9.01563V11.9992H8.01573Z"
                        fill="#E69BFF"
                        fillOpacity="0.8"
                      ></path>
                    </svg>
                    <span>{t('Before_spinning_')}</span>
                  </>
                )}
              </div>
            </div>
            <div className="circle-arrow"></div>
            <div className={`circle-game-btn-play ${isActive && 'active'}`}>
              {getProfile &&
                getProfile.wheelCoins >= getConfig?.wheelSpinCost && (
                  <div className="wheel-pull-lever">
                    {t('wheel_pull_lever')}
                  </div>
                )}
              <div
                onMouseDown={onSignWheel}
                className="circle-game-btn pull"
                onMouseEnter={playAudio}
                data-audio="4.wav"
                data-volume="0.5"
              >
                {getProfile &&
                  getProfile.wheelCoins >= getConfig?.wheelSpinCost && (
                    <>
                      <span className="circle-btn-anim" />
                      <div className="circle-btn-arrow" />
                    </>
                  )}
              </div>
            </div>
            <div className="circle-roulette" style={style}>
              {prizes.map((item) => (
                <div
                  className="circle-roulette-item"
                  data-id={item.id}
                  key={item.id}
                >
                  <img src={item.image} alt={item.name} />
                </div>
              ))}
            </div>
            <CircleControls onSignWheel={onSignWheel} />
          </div>
        </div>

        <div className="page-bonus">
          {getProfile && (
            <div className="bonus-content">
              <div className="bonus-letters-parent-title">
                {t('Super_bonus_')}
              </div>
              <div className="bonus-letters-parent">
                <div className="bonus-letters-parent-left">
                  <div className="bonus-letters-parent-sec-title">
                    {t('Collect_the_word_CSCASE_to_get_a_case_')}
                  </div>
                  <div className="bonus-letters">
                    <div
                      className={`bonus-letters-item ${
                        lettersCounter.C > 0 ? 'active' : ''
                      }`}
                    ></div>
                    <div
                      className={`bonus-letters-item ${
                        lettersCounter.S > 0 ? 'active' : ''
                      }`}
                    ></div>
                    <div
                      className={`bonus-letters-item ${
                        lettersCounter.C > 1 ? 'active' : ''
                      }`}
                    ></div>
                    <div
                      className={`bonus-letters-item ${
                        lettersCounter.A > 0 ? 'active' : ''
                      }`}
                    ></div>
                    <div
                      className={`bonus-letters-item ${
                        lettersCounter.S > 1 ? 'active' : ''
                      }`}
                    ></div>
                    <div
                      className={`bonus-letters-item ${
                        lettersCounter.E > 0 ? 'active' : ''
                      }`}
                    ></div>
                  </div>
                </div>

                <div className="bonus-letters-parent-right">
                  <div className="cases">
                    <div
                      className="case-item"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <a href={caseData?.getCase?.getUrl}>
                        <div className="case-item-img">
                          {isHover ? (
                            <LazyLoadImage
                              alt={caseData?.getName}
                              className="hover"
                              src={caseData?.getCase?.getImage}
                              delayTime={0}
                              //placeholderSrc={image}
                            />
                          ) : (
                            <LazyLoadImage
                              alt={caseData?.getName}
                              className="not-hover"
                              src={caseData?.getCase?.getBackgroundImage}
                              delayTime={0}
                              //placeholderSrc={backgroundImage}
                            />
                          )}
                        </div>
                      </a>
                      <div className="case-item-name">
                        <span>
                          <div className="text-cut">
                            {caseData?.getCase?.getName}
                          </div>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {getProfile && (
                <QuestList
                  getWheelQuests={getWheelQuests}
                  isWheelQuests={true}
                  onQuestComplete={handleQuestComplete}
                />
              )}
            </div>
          )}

          <div className="bonus-prizes">
            <Prizes />
          </div>
        </div>
      </div>
    </>
  );
};

export default Wheel;
